import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  Button,
  useMediaQuery,
  Link,
  Breadcrumbs,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import AppMenu from '../components/AppMenu';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import LoginModal from '../components/LoginModal';
import SignupModal from '../components/SignupModal';

/**
 * Cookie Policy page component
 * 
 * Contains the full cookie policy for the MyEventShop platform
 * detailing how cookies are used and managed
 */
const CookiePolicy: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [isSignupOpen, setIsSignupOpen] = useState(false);

  const handleSwitchToSignup = () => {
    setIsLoginOpen(false);
    setIsSignupOpen(true);
  };

  const handleSwitchToLogin = () => {
    setIsSignupOpen(false);
    setIsLoginOpen(true);
  };
  
  // Last Updated date
  const lastUpdated = "April 5, 2025";

  // Table of contents items
  const tableOfContents = [
    { title: '1. Introduction', id: 'introduction' },
    { title: '2. What Are Cookies', id: 'what-are-cookies' },
    { title: '3. Types of Cookies We Use', id: 'cookie-types' },
    { title: '4. Cookie Duration', id: 'cookie-duration' },
    { title: '5. Third-Party Cookies', id: 'third-party-cookies' },
    { title: '6. How We Use Cookies', id: 'cookie-usage' },
    { title: '7. Managing Your Cookie Preferences', id: 'cookie-preferences' },
    { title: '8. Cookies and Your Privacy', id: 'cookies-privacy' },
    { title: '9. Changes to This Cookie Policy', id: 'changes' },
    { title: '10. Contact Us', id: 'contact' }
  ];

  return (
    <Box>
      <AppMenu 
                onLoginClick={() => setIsLoginOpen(true)}
        onSignupClick={() => setIsSignupOpen(true)} 
        currentPath={location.pathname}
      />
      
      <Container maxWidth="lg">
        {/* Breadcrumbs */}
        <Breadcrumbs 
          separator={<NavigateNextIcon fontSize="small" />} 
          aria-label="breadcrumb"
          sx={{ mt: 3, mb: 2 }}
        >
          <Link 
            underline="hover" 
            color="inherit" 
            onClick={() => navigate('/')}
            sx={{ cursor: 'pointer' }}
          >
            Home
          </Link>
          <Typography color="text.primary">Cookie Policy</Typography>
        </Breadcrumbs>

        {/* Hero Section */}
        <Box
          sx={{
            py: { xs: 4, md: 6 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
          }}
        >
          <Typography 
            variant="h4" 
            component="h1" 
            color="primary" 
            fontWeight="bold"
            gutterBottom
          >
            Cookie Policy
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Last Updated: {lastUpdated}
          </Typography>
        </Box>

        {/* Main Content Container */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, mb: 8 }}>
          {/* Table of Contents */}
          <Box 
            component={Paper} 
            elevation={2}
            sx={{
              p: 3, 
              borderRadius: 2, 
              width: { xs: '100%', md: '300px' },
              flexShrink: 0,
              position: { xs: 'static', md: 'sticky' },
              top: '20px',
              alignSelf: 'flex-start',
              maxHeight: { xs: 'auto', md: 'calc(100vh - 40px)' },
              overflowY: { xs: 'visible', md: 'auto' }
            }}
          >
            <Typography variant="h6" fontWeight="bold" mb={2}>Table of Contents</Typography>
            <List sx={{ p: 0 }}>
              {tableOfContents.map((item) => (
                <ListItem 
                  key={item.id} 
                  disablePadding 
                  sx={{ mb: 1 }}
                >
                  <ListItemText 
                    primary={
                      <Link 
                        href={`#${item.id}`} 
                        underline="hover" 
                        color="text.primary"
                        sx={{ 
                          display: 'block',
                          fontSize: '0.9rem',
                          lineHeight: 1.4,
                        }}
                      >
                        {item.title}
                      </Link>
                    } 
                  />
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Cookie Policy Content */}
          <Box 
            component={Paper}
            elevation={2}
            sx={{ 
              p: { xs: 3, md: 4 }, 
              borderRadius: 2,
              flex: '1 1 auto',
            }}
          >
            <Box id="introduction" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                1. Introduction
              </Typography>
              <Typography variant="body1" paragraph>
                This Cookie Policy explains how MyEventShop ("we," "our," or "us") uses cookies and similar tracking technologies on our website and mobile applications (collectively, the "Services"). This policy provides you with information about how we use cookies, what types of cookies we use, and how you can control cookies through your browser settings.
              </Typography>
              <Typography variant="body1" paragraph>
                By using our Services, you consent to the use of cookies in accordance with this Cookie Policy. If you do not accept the use of cookies, you can disable them, but this may affect your experience on our Services.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="what-are-cookies" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                2. What Are Cookies
              </Typography>
              <Typography variant="body1" paragraph>
                Cookies are small text files that are stored on your device (computer, tablet, or mobile) when you visit a website or use an application. They are widely used to make websites work more efficiently, provide a better user experience, and give website owners information about how users interact with their site.
              </Typography>
              <Typography variant="body1" paragraph>
                Cookies serve many functions, such as allowing you to navigate between pages efficiently, remembering your preferences, and generally improving your user experience. They can also help ensure that advertisements you see online are more relevant to you and your interests.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="cookie-types" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                3. Types of Cookies We Use
              </Typography>
              <Typography variant="body1" paragraph>
                We use several different types of cookies on our Services. These can be categorized based on their purpose:
              </Typography>
              
              <Paper elevation={0} variant="outlined" sx={{ mb: 3 }}>
                <List>
                  <ListItem>
                    <ListItemText primary="Essential/Necessary Cookies" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Functional Cookies" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Performance/Analytics Cookies" />
                  </ListItem>
                  <ListItem>
                    <ListItemText primary="Targeting/Advertising Cookies" />
                  </ListItem>
                </List>
              </Paper>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="cookie-duration" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                4. Cookie Duration
              </Typography>
              <Typography variant="body1" paragraph>
                Based on how long they remain active, cookies can be classified as:
              </Typography>
              
              <Typography variant="subtitle1" fontWeight="medium" mt={2} mb={1}>
                A. Session Cookies
              </Typography>
              <Typography variant="body1" paragraph>
                These cookies are temporary and are deleted from your device when you close your web browser. They are used to remember you during a single browsing session.
              </Typography>
              
              <Typography variant="subtitle1" fontWeight="medium" mt={2} mb={1}>
                B. Persistent Cookies
              </Typography>
              <Typography variant="body1" paragraph>
                These cookies remain on your device for a specified period or until you delete them manually. They are activated each time you visit the website that created the cookie and help websites remember your actions and preferences over time.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="third-party-cookies" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                5. Third-Party Cookies
              </Typography>
              <Typography variant="body1" paragraph>
                In addition to our own cookies, we may also use various third-party cookies to report usage statistics, deliver advertisements, and provide other services:
              </Typography>
              <List sx={{ pl: 2, listStyleType: 'disc' }}>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1"><strong>Analytics providers</strong> such as Google Analytics, which help us understand how visitors interact with our Services</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1"><strong>Social media platforms</strong> like Facebook, Twitter, and Instagram, which enable social sharing features</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1"><strong>Advertising networks</strong> that deliver targeted advertisements based on your preferences</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1"><strong>Payment processors</strong> that enable secure transactions</Typography>
                </ListItem>
              </List>
              <Typography variant="body1" mt={2} paragraph>
                These third parties may use cookies, pixel tags, or other technologies to collect information about your use of our Services and other websites. This information may be used to provide services, display advertisements based on your interests, analyze the effectiveness of our Services, and for other purposes outlined in their respective privacy policies.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="cookie-usage" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                6. How We Use Cookies
              </Typography>
              <Typography variant="body1" paragraph>
                We use cookies for a variety of reasons, including to:
              </Typography>
              <List sx={{ pl: 2, listStyleType: 'disc' }}>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Authenticate users and maintain sessions</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Remember your preferences and settings</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Enhance site navigation and provide personalized features</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Track your use of our Services to help us improve them</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Analyze the success of our marketing campaigns</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Provide content and advertisements tailored to your interests</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Process payments and maintain transaction records</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Prevent fraud and enhance security</Typography>
                </ListItem>
              </List>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="cookie-preferences" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                7. Managing Your Cookie Preferences
              </Typography>
              <Typography variant="body1" paragraph>
                You have the right to choose whether to accept cookies. While most browsers are initially set to accept cookies, you can modify your browser settings to reflect your cookie preferences.
              </Typography>
              
              <Typography variant="subtitle1" fontWeight="medium" mt={2} mb={1}>
                A. Browser Controls
              </Typography>
              <Typography variant="body1" paragraph>
                Most browsers allow you to manage your cookie preferences by:
              </Typography>
              <List sx={{ pl: 2, listStyleType: 'disc' }}>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Deleting cookies from your device</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Blocking cookies by default</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Allowing cookies from specific websites only</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Blocking third-party cookies</Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1">Using private or incognito browsing mode</Typography>
                </ListItem>
              </List>
              <Typography variant="body1" mt={2} paragraph>
                You can find information on how to manage cookie settings in your browser here:
              </Typography>
              <List sx={{ pl: 2, listStyleType: 'disc' }}>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1"><Link href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Google Chrome</Link></Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1"><Link href="https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop" target="_blank" rel="noopener noreferrer">Mozilla Firefox</Link></Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1"><Link href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac" target="_blank" rel="noopener noreferrer">Apple Safari</Link></Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1"><Link href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer">Microsoft Edge</Link></Typography>
                </ListItem>
              </List>
              
              <Typography variant="subtitle1" fontWeight="medium" mt={3} mb={1}>
                B. Opt-Out of Targeted Advertising
              </Typography>
              <Typography variant="body1" paragraph>
                You can opt out of interest-based advertising from advertising networks that may use your information through the following mechanisms:
              </Typography>
              <List sx={{ pl: 2, listStyleType: 'disc' }}>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1"><Link href="https://optout.aboutads.info/" target="_blank" rel="noopener noreferrer">Digital Advertising Alliance (DAA)</Link></Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1"><Link href="https://optout.networkadvertising.org/" target="_blank" rel="noopener noreferrer">Network Advertising Initiative (NAI)</Link></Typography>
                </ListItem>
                <ListItem sx={{ display: 'list-item', py: 0.5 }}>
                  <Typography variant="body1"><Link href="https://www.youronlinechoices.com/" target="_blank" rel="noopener noreferrer">European Interactive Digital Advertising Alliance (EDAA)</Link></Typography>
                </ListItem>
              </List>
              
              <Typography variant="subtitle1" fontWeight="medium" mt={3} mb={1}>
                C. Consequences of Disabling Cookies
              </Typography>
              <Typography variant="body1" paragraph>
                Please note that if you choose to disable cookies, some features of our Services may not function properly. In particular, disabling essential cookies may prevent you from using certain features or accessing secure areas of our Services.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="cookies-privacy" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                8. Cookies and Your Privacy
              </Typography>
              <Typography variant="body1" paragraph>
                The information collected through cookies is generally not personally identifiable on its own. However, when combined with other information, it may be used to identify you.
              </Typography>
              <Typography variant="body1" paragraph>
                We take data privacy seriously and handle all information collected through cookies in accordance with our <Link href="/privacy" color="primary">Privacy Policy</Link>. Our Privacy Policy provides more detailed information about how we collect, use, and protect your personal information.
              </Typography>
              <Typography variant="body1" paragraph>
                By using our Services, you acknowledge that we may collect and use your data in line with this Cookie Policy and our Privacy Policy.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="changes" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                9. Changes to This Cookie Policy
              </Typography>
              <Typography variant="body1" paragraph>
                We may update this Cookie Policy from time to time to reflect changes in technology, regulation, or our business practices. Any changes will become effective when we post the revised policy on this page.
              </Typography>
              <Typography variant="body1" paragraph>
                You can determine when this Cookie Policy was last revised by checking the "Last Updated" date at the top of this page. We encourage you to review this Cookie Policy periodically to stay informed about our use of cookies.
              </Typography>
              <Typography variant="body1" paragraph>
                By continuing to use our Services after we post any changes to this Cookie Policy, you accept the updated policy.
              </Typography>
              <Divider sx={{ my: 3 }} />
            </Box>

            <Box id="contact" sx={{ scrollMarginTop: '100px' }}>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                10. Contact Us
              </Typography>
              <Typography variant="body1" paragraph>
                If you have any questions or concerns about this Cookie Policy or our use of cookies, please contact us at:
              </Typography>
              <Typography variant="body1" paragraph>
                MyEventShop<br />
                123 Barrow Street<br />
                Dublin, DO4 V4X7<br />
                Ireland<br /><br />
                Email: privacy@myeventshop.com<br />
                Phone: +353 (55) 123-4567
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Call to Action */}
        <Paper 
          elevation={3}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 2,
            mb: 8,
            bgcolor: 'rgba(95, 60, 111, 0.05)',
            textAlign: 'center',
          }}
        >
          <Typography variant="h6" component="p" mb={3}>
            Have questions about how we use cookies? We're here to help.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={() => navigate('/contact')}
            sx={{
              px: 4,
              py: 1,
              fontWeight: 'medium',
            }}
          >
            Contact Us
          </Button>
        </Paper>
      </Container>
      
      {/* Authentication Modals */}
      <LoginModal 
        open={isLoginOpen} 
        onClose={() => setIsLoginOpen(false)} 
        onSwitchToSignup={handleSwitchToSignup}
      />
      <SignupModal 
        open={isSignupOpen} 
        onClose={() => setIsSignupOpen(false)} 
        onSwitchToLogin={handleSwitchToLogin}
      />
    </Box>
  );
};

export default CookiePolicy; 